import { Component, Watch } from 'vue-property-decorator';
import { DebouncedFunc, cloneDeep, debounce, find } from 'lodash';
import { format } from 'date-fns';
import AbstractView from '@/views/abstract-view';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Job from '@/model/entity/job/job';
import JobDto from '@/model/entity/job/job-dto';
import JobIndex from '@/components/job-index/index.vue';
import JobPopupForm from '@/components/job-popup-form/index.vue';
import JobService from '@/model/service/job/job-service';
import Techspec from '@/model/entity/job/techspec';
import TechspecService from '@/model/service/job/techspec-service';

@Component({
    components: {
        'app-job-index': JobIndex,
        'app-job-popup-form': JobPopupForm,
    },
})
export default class MeetingView extends AbstractView
{
    private meeting: Techspec|null = null;
    private tab = 'jobs';
    private options = false;
    private duplicationDialog = false;

    private form: FormInterface|null = null;
    private clientId: number|null = null;
    private clientMeetings: Techspec[] = [];

    private showPopupJob = false;
    private popupJob: Job|null = null;

    private updatedNotes: string|null = null;
    private notesSaved = false;

    private meetingUpdatedListener!: (meeting: Techspec) => void;

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        jobs: any
    };

    protected get title(): string
    {
        return this.meeting?.name ?? this.defaultTitle;
    }

    private debouncedUpdateMeeting!: DebouncedFunc<() => Promise<void>>;

    protected created()
    {
        this.meetingUpdatedListener = (meeting: Techspec) =>
        {
            this.meeting = new Techspec(cloneDeep(meeting.dto));
            this.generateForm();
        };

        this.debouncedUpdateMeeting = debounce(
            this.saveMeeting, 4000, { maxWait: 15000 },
        );

        this.loadMeeting();

        EventBus.instance.$on('meeting-updated', this.meetingUpdatedListener);
    }

    protected beforeDestroy()
    {
        EventBus.instance.$off('meeting-updated', this.meetingUpdatedListener);
    }

    @Watch('$route')
    private onRouteChanged()
    {
        if (this.meeting?.id !== parseInt(this.$route.params.id))
        {
            this.loadMeeting();
        }
    }

    /**
     * Returns the client meeting options
     */

    private get clientMeetingOptions(): FormFieldOptionInterface[]
    {
        const options: FormFieldOptionInterface[] = [];

        for (const meeting of this.clientMeetings)
        {
            if (!meeting.meetingDate)
            {
                continue;
            }

            const date = format(meeting.meetingDate, 'yyyy-MM-dd HH:mm');

            options.push({
                value: date,
                title: date,
            });
        }

        return options;
    }

    /**
     * Generates the quick edit form
     *
     * @returns
     */

    private generateForm()
    {
        if (!this.meeting)
        {
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'client',
                    type: 'integer',
                    title: this.$t('general.field.client').toString(),
                    value: this.meeting.clientId,
                    required: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                },
                {
                    key: 'date',
                    type: 'string',
                    title: this.$t('general.field.date').toString(),
                    value: this.meeting.meetingDate ? format(this.meeting.meetingDate, 'yyyy-MM-dd HH:mm') : null,
                    required: true,
                    options: this.clientMeetingOptions,
                    dropdown: true,
                    dependencies: {
                        client: /[0-9]+/,
                    },
                    disableNotHide: true,
                },
                {
                    key: 'location',
                    type: 'string',
                    title: this.$t('meeting.field.meeting_location').toString(),
                    value: TechspecService.getMeetingLocation(this.meeting),
                    required: true,
                    readonly: true,
                },
                {
                    key: 'added_by',
                    type: 'string',
                    title: this.$t('meeting.field.added_by').toString(),
                    value: this.meeting.manager?.fullName ?? '',
                    required: true,
                    readonly: true,
                },
                {
                    key: 'notes',
                    type: 'string',
                    title: this.$t('general.field.notes').toString(),
                    value: this.meeting.description,
                    required: false,
                    readonly: false,
                    multiline: true,
                    rich: true,
                    markdown: true,
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Loads the meeting
     */

    private async loadMeeting()
    {
        try
        {
            this.meeting = await this.$serviceContainer.techspecService.loadTechspec(this.$route.params.id);
            if (!this.meeting)
            {
                return;
            }

            if (this.clientId !== this.meeting.clientId)
            {
                this.clientId = this.meeting.clientId;

                await this.loadClientMeetings();
            }

            this.generateForm();
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_loading_server_error'),
                'error',
            );
        }
    }

    /**
     * Loads meetings for the selected client
     */

    private async loadClientMeetings()
    {
        if (!this.clientId)
        {
            this.clientMeetings = [];
            return;
        }

        const results = await this.$serviceContainer.techspecService.findTechspecs({
            client: `${ this.clientId }`,
            meeting: true,
            order: [
                {
                    key: 'meetingDate',
                    descending: true,
                },
            ],
        });

        this.clientMeetings = results.results;
    }

    /**
     * Handles quick edit value changes
     */

    private async onQuickEditValuesChanged(values: FormFieldValuesInterface)
    {
        // Client changed
        if ((values.client as number) !== this.clientId)
        {
            this.clientId = values.client as number;

            await this.loadClientMeetings();

            if (this.clientMeetings.length > 0 && this.clientMeetings[0].id)
            {
                this.meeting = await this.$serviceContainer.techspecService.loadTechspec(this.clientMeetings[0].id);
            }
            else
            {
                this.meeting = null;
            }

            this.updateRoute();
            this.generateForm();

            // If the client has changed, then don't allow further checks
            return;
        }

        // Meeting changed
        const date = (values.date as string);
        const meetingDate = this.meeting?.meetingDate ? format(this.meeting.meetingDate, 'yyyy-MM-dd HH:mm') : null;

        if (date !== meetingDate)
        {
            const meeting = find(this.clientMeetings, (meeting: Techspec) =>
            {
                if (!meeting.meetingDate)
                {
                    return false;
                }

                return format(meeting.meetingDate, 'yyyy-MM-dd HH:mm') === date;
            });

            if (meeting?.id)
            {
                this.meeting = await this.$serviceContainer.techspecService.loadTechspec(meeting.id);
                this.updateRoute();
                this.generateForm();

                return;
            }
        }

        // Notes changed
        if (this.meeting)
        {
            const notes = values.notes as string;
            if (notes !== this.meeting.description)
            {
                this.updatedNotes = notes;

                this.debouncedUpdateMeeting();
            }
        }
    }

    /**
     * Updates the route to match the current meeting
     */

    private updateRoute()
    {
        if (!this.meeting?.id)
        {
            return;
        }

        if (this.meeting.id !== parseInt(this.$route.params.id))
        {
            this.$router.replace({
                params: {
                    id: this.meeting.id.toString(),
                },
            }).catch();
        }
    }

    /**
     * Handles editing the meeting
     */

    private onEditMeeting()
    {
        EventBus.instance.$emit('edit-meeting', this.meeting);
    }

    /**
     * Saves the meeting
     *
     * @returns
     */

    private async saveMeeting()
    {
        if (!this.meeting)
        {
            return;
        }

        try
        {
            // Set the updated notes
            this.meeting.description = this.updatedNotes;
            this.updatedNotes = null;
            this.notesSaved = true;

            // Save the meeting
            this.meeting = await this.$serviceContainer.techspecService.updateTechspec(this.meeting);
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_saving_server_error'),
                'error',
            );
        }
    }

    /**
     * Handles adding a new job
     *
     * @param forClient
     * @returns
     */

    private onNewJob(forClient = false)
    {
        if (!this.meeting?.id)
        {
            return;
        }

        this.popupJob = new Job(new JobDto());
        this.popupJob.clientId = this.meeting.clientId;
        this.popupJob.techspecId = this.meeting.id;
        this.popupJob.clientUserId = this.meeting.clientUserId;
        this.popupJob.managerId = this.$user?.id ?? null;
        this.popupJob.type = 'Meeting Job';
        this.popupJob.priority = 3;
        this.popupJob.stageId = forClient ? JobService.STAGE_WAITING_FOR_CLIENT : JobService.STAGE_STANDARD_SUPPORT;
        this.popupJob.assigneeId = this.$user?.id ?? null;
        this.popupJob.clientVisible = true;

        this.showPopupJob = true;
    }

    /**
     * Handles editing a job
     *
     * @param job
     */

    private async onEditJob(job: Job)
    {
        if (!job.id)
        {
            return;
        }

        const fullJob = await this.$serviceContainer.jobService.loadJob(job.id);
        if (!fullJob)
        {
            return;
        }

        this.popupJob = fullJob;
        this.showPopupJob = true;
    }

    /**
     * Handles jobs being saved
     */

    private onJobSaved()
    {
        this.$refs.jobs.reload();
    }
}